import { useEffect, useRef, useState, useCallback } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import type Konva from 'konva'
import { Stage, Layer, Circle, Rect, Line, Text, Group, Image as KonvaImage, Arrow, Transformer } from 'react-konva'
import { ArrowLeft, Save, Download, MousePointer2, Flower2, Spade, Square, Ruler, Type, Layers, Search, X, Plus, Minus, RotateCcw, Leaf, ZoomIn, ZoomOut } from 'lucide-react'
import { Spinner } from '../components/shared/ui'
import { currency } from '../lib/format'
import { getSitePlan, saveSitePlan, listProductsForPalette } from '../services/data'
import type { SitePlan, SitePlanItem, Product } from '../types'
import jsPDF from 'jspdf'

// ─── Constants & Types ───────────────────────────────────────────────────────
const CANVAS_W = 1200
const CANVAS_H = 800
const GOOGLE_MAPS_KEY = import.meta.env.VITE_GOOGLE_MAPS_KEY as string | undefined
const MIN_SCALE = 0.25, MAX_SCALE = 4

type ToolType = 'select' | 'plant' | 'bed' | 'hardscape' | 'polygon' | 'dimension' | 'label'
type LayerType = 'existing' | 'proposed' | 'phase1' | 'phase2'
type ScaleCalib = { p1:{x:number;y:number}; p2:{x:number;y:number}; feet:number } | null

const LAYER_COLORS: Record<LayerType, string> = {
  existing: '#94a3b8', proposed: '#22c55e', phase1: '#3b82f6', phase2: '#f59e0b',
}
const KEY_MAP: Record<string, ToolType> = {
  v:'select', p:'plant', b:'bed', h:'hardscape', g:'polygon', d:'dimension', l:'label',
}
const TOOL_BTNS: { key: ToolType; label: string; icon: React.ReactNode; tip: string }[] = [
  { key:'select',    label:'Select',    icon:<MousePointer2 size={16}/>, tip:'Select & move (V)' },
  { key:'plant',     label:'Plant',     icon:<Flower2 size={16}/>,       tip:'Place plant — pick from palette first (P)' },
  { key:'bed',       label:'Bed',       icon:<Spade size={16}/>,         tip:'Drag to draw bed (B)' },
  { key:'hardscape', label:'Hardscape', icon:<Square size={16}/>,        tip:'Drag to draw hardscape (H)' },
  { key:'polygon',   label:'Polygon',   icon:<Layers size={16}/>,        tip:'Click points, double-click to close (G)' },
  { key:'dimension', label:'Measure',   icon:<Ruler size={16}/>,         tip:'Drag to measure distance (D)' },
  { key:'label',     label:'Label',     icon:<Type size={16}/>,          tip:'Click to place text (L)' },
]

function dist(x1:number,y1:number,x2:number,y2:number){ return Math.sqrt((x2-x1)**2+(y2-y1)**2) }
function pxToFt(px:number,c:ScaleCalib){ if(!c) return `${Math.round(px)}px`; const ppf=dist(c.p1.x,c.p1.y,c.p2.x,c.p2.y)/c.feet; return `${(px/ppf).toFixed(1)} ft` }

// ─── Sub-components ──────────────────────────────────────────────────────────

function PlantSymbol({ item, isSelected, onSelect, onChange }: {
  item: SitePlanItem; isSelected: boolean
  onSelect: () => void
  onChange: (attrs: Record<string,unknown>) => void
}) {
  const groupRef = useRef<Konva.Group>(null)
  const trRef = useRef<Konva.Transformer>(null)
  useEffect(() => {
    if (isSelected && trRef.current && groupRef.current) {
      trRef.current.nodes([groupRef.current])
      trRef.current.getLayer()?.batchDraw()
    }
  }, [isSelected])
  const r = (item.attrs.radius as number) ?? 28
  const color = item.color ?? LAYER_COLORS[item.layer as LayerType] ?? '#22c55e'
  const sym = item.symbol_code ?? '?'
  return (<>
    <Group ref={groupRef}
      x={(item.attrs.x as number)??100} y={(item.attrs.y as number)??100}
      draggable
      onClick={e=>{e.cancelBubble=true;onSelect()}}
      onTap={e=>{e.cancelBubble=true;onSelect()}}
      onDragEnd={e=>onChange({...item.attrs,x:e.target.x(),y:e.target.y()})}
      onTransformEnd={()=>{ const n=groupRef.current!; onChange({...item.attrs,x:n.x(),y:n.y(),scaleX:n.scaleX(),scaleY:n.scaleY()}) }}
    >
      <Circle radius={r} fill={color+'33'} stroke={color} strokeWidth={isSelected?2.5:1.5} dash={[5,3]}/>
      <Circle radius={r*0.28} fill={color}/>
      <Text text={sym} fontSize={Math.max(9,r*0.38)} fill="#fff" fontStyle="bold"
        align="center" verticalAlign="middle" width={r*0.56} height={r*0.56} offsetX={r*0.28} offsetY={r*0.28}/>
      <Text text={item.label??''} fontSize={10} fill={color} fontStyle="bold"
        align="center" width={r*3} offsetX={r*1.5} y={r+4} listening={false}/>
    </Group>
    {isSelected && <Transformer ref={trRef} rotateEnabled={false}
      enabledAnchors={['top-left','top-right','bottom-left','bottom-right']}
      boundBoxFunc={(_,nb)=>({...nb,width:Math.max(20,nb.width),height:Math.max(20,nb.height)})}/>
    }
  </>)
}

function RectShape({ item, isSelected, onSelect, onChange }: {
  item: SitePlanItem; isSelected: boolean
  onSelect: () => void
  onChange: (attrs: Record<string,unknown>) => void
}) {
  const rRef = useRef<Konva.Rect>(null)
  const trRef = useRef<Konva.Transformer>(null)
  useEffect(() => {
    if (isSelected && trRef.current && rRef.current) {
      trRef.current.nodes([rRef.current])
      trRef.current.getLayer()?.batchDraw()
    }
  }, [isSelected])
  const color = item.color ?? (item.item_type==='bed'?'#6abf4b':'#b0a090')
  const isBed = item.item_type==='bed'
  return (<>
    <Rect ref={rRef}
      x={(item.attrs.x as number)??100} y={(item.attrs.y as number)??100}
      width={(item.attrs.width as number)??120} height={(item.attrs.height as number)??80}
      fill={color+'44'} stroke={color} strokeWidth={isSelected?2.5:1.8}
      cornerRadius={isBed?10:2} dash={isBed?[8,4]:undefined}
      draggable
      onClick={e=>{e.cancelBubble=true;onSelect()}}
      onTap={e=>{e.cancelBubble=true;onSelect()}}
      onDragEnd={e=>onChange({...item.attrs,x:e.target.x(),y:e.target.y()})}
      onTransformEnd={()=>{
        const n=rRef.current!
        onChange({...item.attrs,x:n.x(),y:n.y(),width:Math.max(10,n.width()*n.scaleX()),height:Math.max(10,n.height()*n.scaleY())})
        n.scaleX(1);n.scaleY(1)
      }}
    />
    {item.label && <Text x={(item.attrs.x as number??100)+5} y={(item.attrs.y as number??100)+5}
      text={item.label} fontSize={11} fill={color} fontStyle="bold" listening={false}/>}
    {isSelected && <Transformer ref={trRef} rotateEnabled={false}
      boundBoxFunc={(_,nb)=>({...nb,width:Math.max(20,nb.width),height:Math.max(20,nb.height)})}/>
    }
  </>)
}

function PolyShape({ item, isSelected, onSelect, onChange }: {
  item: SitePlanItem; isSelected: boolean
  onSelect: () => void
  onChange: (attrs: Record<string,unknown>) => void
}) {
  const pts = (item.attrs.points as number[]) ?? []
  const color = item.color ?? '#6abf4b'
  if (pts.length < 4) return null
  return (<>
    <Line points={pts} closed fill={color+'44'} stroke={color}
      strokeWidth={isSelected?2.5:1.8} dash={[8,4]}
      draggable
      onClick={e=>{e.cancelBubble=true;onSelect()}}
      onTap={e=>{e.cancelBubble=true;onSelect()}}
      onDragEnd={e=>{
        const node=e.target as Konva.Line
        const dx=node.x(),dy=node.y()
        const newPts=pts.map((v,i)=>i%2===0?v+dx:v+dy)
        node.position({x:0,y:0})
        onChange({...item.attrs,points:newPts})
      }}
    />
    {item.label && pts.length>=2 && <Text x={pts[0]} y={pts[1]-14}
      text={item.label} fontSize={11} fill={color} fontStyle="bold" listening={false}/>}
  </>)
}

function DimShape({ item, isSelected, onSelect, calib }: {
  item: SitePlanItem; isSelected: boolean
  onSelect: () => void
  calib: ScaleCalib
}) {
  const x1=(item.attrs.x1 as number)??0, y1=(item.attrs.y1 as number)??0
  const x2=(item.attrs.x2 as number)??100, y2=(item.attrs.y2 as number)??0
  const d=dist(x1,y1,x2,y2)
  const label=pxToFt(d,calib)
  const mx=(x1+x2)/2, my=(y1+y2)/2
  const angle=Math.atan2(y2-y1,x2-x1)*180/Math.PI
  const col=isSelected?'#1d4ed8':'#fff'
  const perp=(angle+90)*Math.PI/180
  const tx=Math.cos(perp)*7, ty=Math.sin(perp)*7
  return (
    <Group onClick={e=>{e.cancelBubble=true;onSelect()}}>
      <Arrow points={[x1,y1,x2,y2]} stroke={col} strokeWidth={1.5} fill={col}
        pointerLength={8} pointerWidth={6} pointerAtBeginning/>
      <Line points={[x1-tx,y1-ty,x1+tx,y1+ty]} stroke={col} strokeWidth={1.5}/>
      <Line points={[x2-tx,y2-ty,x2+tx,y2+ty]} stroke={col} strokeWidth={1.5}/>
      <Rect x={mx-label.length*3.5-2} y={my-10} width={label.length*7+4} height={16}
        fill="rgba(0,0,0,0.65)" cornerRadius={3} listening={false}/>
      <Text x={mx-label.length*3.5} y={my-9} text={label} fontSize={11} fill="#fff"
        fontStyle="bold" listening={false}/>
    </Group>
  )
}

function LabelShape({ item, isSelected, onSelect, onChange }: {
  item: SitePlanItem; isSelected: boolean
  onSelect: () => void
  onChange: (attrs: Record<string,unknown>) => void
}) {
  return (
    <Text x={(item.attrs.x as number)??100} y={(item.attrs.y as number)??100}
      text={item.label??'Label'} fontSize={14}
      fill={isSelected?'#facc15':'#fff'} fontStyle={isSelected?'bold':'normal'}
      shadowColor="#000" shadowBlur={3} shadowOpacity={0.8}
      draggable
      onClick={e=>{e.cancelBubble=true;onSelect()}}
      onTap={e=>{e.cancelBubble=true;onSelect()}}
      onDragEnd={e=>onChange({...item.attrs,x:e.target.x(),y:e.target.y()})}
    />
  )
}

// ─── Main Editor ─────────────────────────────────────────────────────────────
export default function SitePlanEditor() {
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const isNew = id === 'new'

  const [plan, setPlan] = useState<SitePlan | null>(null)
  const [items, setItems] = useState<SitePlanItem[]>([])
  const [history, setHistory] = useState<SitePlanItem[][]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [savedOk, setSavedOk] = useState(false)

  const [tool, setTool] = useState<ToolType>('select')
  const [activeLayer, setActiveLayer] = useState<LayerType>('proposed')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [visibleLayers, setVisibleLayers] = useState<Set<LayerType>>(
    new Set<LayerType>(['existing', 'proposed', 'phase1', 'phase2'])
  )

  // Drag-to-draw state (bed/hardscape)
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null)
  const [dragCurrent, setDragCurrent] = useState<{ x: number; y: number } | null>(null)

  // Dimension draw state
  const [dimStart, setDimStart] = useState<{ x: number; y: number } | null>(null)
  const [dimCurrent, setDimCurrent] = useState<{ x: number; y: number } | null>(null)

  // Polygon draw state
  const [polyPts, setPolyPts] = useState<number[]>([])
  const [polyMouse, setPolyMouse] = useState<{ x: number; y: number } | null>(null)

  // Scale calibration
  const [calib, setCalib] = useState<ScaleCalib>(null)
  const [calibMode, setCalibMode] = useState<'idle' | 'p1' | 'p2'>('idle')
  const [calibP1, setCalibP1] = useState<{ x: number; y: number } | null>(null)
  const [calibFeet, setCalibFeet] = useState('30')
  const [showCalib, setShowCalib] = useState(false)

  // Zoom/pan
  const [stageScale, setStageScale] = useState(1)
  const [stagePos, setStagePos] = useState({ x: 0, y: 0 })

  // Right panel
  const [paletteSearch, setPaletteSearch] = useState('')
  const [paletteItems, setPaletteItems] = useState<Product[]>([])
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)
  const [activeTab, setActiveTab] = useState<'palette' | 'schedule'>('palette')

  // Map
  const [address, setAddress] = useState('')
  const [mapUrl, setMapUrl] = useState<string | null>(null)
  const [mapImage, setMapImage] = useState<HTMLImageElement | null>(null)

  // Plan metadata
  const [planName, setPlanName] = useState('New Site Plan')
  const [versionLabel, setVersionLabel] = useState('')

  const stageRef = useRef<Konva.Stage>(null)
  const nextSymbol = useRef(65)

  // ── Keyboard shortcuts ──
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement).tagName === 'INPUT') return
      const t = KEY_MAP[e.key.toLowerCase()]
      if (t) { setTool(t); return }
      if (e.key === 'Delete' || e.key === 'Backspace') deleteSelected()
      if ((e.metaKey || e.ctrlKey) && e.key === 'z') handleUndo()
      if (e.key === 'Escape') { setPolyPts([]); setPolyMouse(null); setTool('select') }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  // ── Load ──
  useEffect(() => {
    async function load() {
      setLoading(true)
      try {
        if (!isNew && id) {
          const data = await getSitePlan(id)
          if (data) {
            setPlan(data.plan)
            setItems(data.items)
            setPlanName(data.plan.name)
            setVersionLabel(data.plan.version_label ?? '')
            setAddress(data.plan.address ?? '')
            if (data.plan.map_snapshot_url) loadMapFromUrl(data.plan.map_snapshot_url)
            if (data.plan.pixels_per_foot) {
              setCalib({ p1:{x:0,y:0}, p2:{x:data.plan.pixels_per_foot,y:0}, feet:1 })
            }
            const maxCode = data.items.reduce((m, it) => {
              if (it.symbol_code?.length === 1) return Math.max(m, it.symbol_code.charCodeAt(0) + 1)
              return m
            }, 65)
            nextSymbol.current = maxCode
          }
        } else {
          const addr = searchParams.get('address')
          if (addr) setAddress(addr)
        }
        // Initial palette load is handled by the debounced search effect.
      } finally { setLoading(false) }
    }
    load()
  }, [id, isNew])

  // ── Palette search (server-side, debounced) ──
  useEffect(() => {
    let active = true
    const h = setTimeout(() => {
      listProductsForPalette(paletteSearch)
        .then(p => { if (active) setPaletteItems(p) })
        .catch(() => { if (active) setPaletteItems([]) })
    }, 250)
    return () => { active = false; clearTimeout(h) }
  }, [paletteSearch])

  // ── Map ──
  function loadMapFromUrl(url: string) {
    setMapUrl(url)
    const img = new window.Image()
    img.crossOrigin = 'anonymous'
    img.src = url
    img.onload = () => setMapImage(img)
  }
  const loadMap = useCallback(() => {
    if (!address.trim() || !GOOGLE_MAPS_KEY) return
    const url = `https://maps.googleapis.com/maps/api/staticmap?center=${encodeURIComponent(address)}&zoom=19&size=${CANVAS_W}x${CANVAS_H}&maptype=satellite&key=${GOOGLE_MAPS_KEY}`
    loadMapFromUrl(url); setMapUrl(url)
  }, [address])

  // ── History ──
  function pushHistory(snap: SitePlanItem[]) { setHistory(h => [...h.slice(-19), snap]) }
  function handleUndo() {
    if (!history.length) return
    setItems(history[history.length - 1])
    setHistory(h => h.slice(0, -1))
    setSelectedId(null)
  }

  // ── Item helpers ──
  function addItem(partial: Partial<SitePlanItem>) {
    const newItem: SitePlanItem = {
      id: crypto.randomUUID(), site_plan_id: id ?? '',
      item_type: 'label', layer: activeLayer,
      product_id: null, product_name: null, botanical_name: null,
      attrs: {}, spread_ft: null, depth_in: null, qty: 1,
      unit_price: null, size_spec: null, label: null,
      symbol_code: null, color: null, sort_order: items.length,
      created_at: new Date().toISOString(), ...partial,
    }
    pushHistory(items)
    setItems(prev => [...prev, newItem])
    setSelectedId(newItem.id)
  }

  function changeItemAttrs(itemId: string, attrs: Record<string, unknown>) {
    setItems(prev => prev.map(it => it.id === itemId ? { ...it, attrs } : it))
  }

  function deleteSelected() {
    if (!selectedId) return
    pushHistory(items)
    setItems(prev => prev.filter(it => it.id !== selectedId))
    setSelectedId(null)
  }

  // ── Convert stage pointer to canvas coords accounting for zoom/pan ──
  function stageCoords(stage: Konva.Stage): { x: number; y: number } {
    const p = stage.getPointerPosition()!
    return { x: (p.x - stagePos.x) / stageScale, y: (p.y - stagePos.y) / stageScale }
  }

  // ── Wheel zoom ──
  function handleWheel(e: Konva.KonvaEventObject<WheelEvent>) {
    e.evt.preventDefault()
    const stage = stageRef.current!
    const ptr = stage.getPointerPosition()!
    const origin = { x: (ptr.x - stagePos.x) / stageScale, y: (ptr.y - stagePos.y) / stageScale }
    const ns = Math.min(MAX_SCALE, Math.max(MIN_SCALE, stageScale * (e.evt.deltaY < 0 ? 1.12 : 0.9)))
    setStageScale(ns)
    setStagePos({ x: ptr.x - origin.x * ns, y: ptr.y - origin.y * ns })
  }

  // ── Stage mouse events ──
  function handleMouseDown(e: Konva.KonvaEventObject<MouseEvent>) {
    const stage = e.target.getStage()!
    const pos = stageCoords(stage)

    if (calibMode === 'p1') { setCalibP1(pos); setCalibMode('p2'); return }
    if (calibMode === 'p2' && calibP1) {
      const ft = parseFloat(calibFeet)
      if (!isNaN(ft) && ft > 0) setCalib({ p1: calibP1, p2: pos, feet: ft })
      setCalibMode('idle'); return
    }

    if (tool === 'bed' || tool === 'hardscape') { setDragStart(pos); setDragCurrent(pos) }
    if (tool === 'dimension') { setDimStart(pos); setDimCurrent(pos) }
    if (tool === 'polygon') {
      setPolyPts(prev => [...prev, pos.x, pos.y])
      setPolyMouse(pos)
    }
    if (tool === 'select' && e.target === stage) setSelectedId(null)
  }

  function handleMouseMove(e: Konva.KonvaEventObject<MouseEvent>) {
    const pos = stageCoords(e.target.getStage()!)
    if (dragStart) setDragCurrent(pos)
    if (dimStart) setDimCurrent(pos)
    if (tool === 'polygon') setPolyMouse(pos)
  }

  function handleMouseUp(e: Konva.KonvaEventObject<MouseEvent>) {
    const pos = stageCoords(e.target.getStage()!)
    if (dragStart && (tool === 'bed' || tool === 'hardscape')) {
      const x = Math.min(dragStart.x, pos.x), y = Math.min(dragStart.y, pos.y)
      const w = Math.abs(pos.x - dragStart.x), h = Math.abs(pos.y - dragStart.y)
      if (w > 6 && h > 6) addItem({
        item_type: tool, layer: activeLayer,
        label: tool === 'bed' ? 'Planting Bed' : 'Hardscape',
        color: tool === 'bed' ? '#6abf4b' : '#b0a090',
        attrs: { x, y, width: w, height: h },
      })
      setDragStart(null); setDragCurrent(null)
    }
    if (dimStart && tool === 'dimension') {
      const d = dist(dimStart.x, dimStart.y, pos.x, pos.y)
      if (d > 8) addItem({
        item_type: 'dimension', layer: activeLayer,
        label: pxToFt(d, calib),
        attrs: { x1: dimStart.x, y1: dimStart.y, x2: pos.x, y2: pos.y },
      })
      setDimStart(null); setDimCurrent(null)
    }
  }

  function handleClick(e: Konva.KonvaEventObject<MouseEvent>) {
    if (e.target !== e.target.getStage()) return
    const pos = stageCoords(e.target.getStage()!)
    if (tool === 'plant' && selectedProduct) {
      addItem({
        item_type: 'plant', layer: activeLayer,
        product_id: selectedProduct.id,
        product_name: String(selectedProduct.product_name ?? selectedProduct.name ?? ''),
        label: String(selectedProduct.product_name ?? selectedProduct.name ?? ''),
        symbol_code: String.fromCharCode(nextSymbol.current++),
        qty: 1,
        unit_price: selectedProduct.retail_price != null ? Number(selectedProduct.retail_price) : null,
        color: LAYER_COLORS[activeLayer],
        attrs: { x: pos.x, y: pos.y, radius: 28 },
      })
    }
    if (tool === 'label') {
      addItem({ item_type: 'label', layer: activeLayer, label: 'Label', attrs: { x: pos.x, y: pos.y } })
      setTool('select')
    }
  }

  function handleDblClick(_e: Konva.KonvaEventObject<MouseEvent>) {
    if (tool === 'polygon' && polyPts.length >= 6) {
      addItem({
        item_type: 'bed', layer: activeLayer,
        label: 'Planting Bed', color: '#6abf4b',
        attrs: { points: polyPts, isPolygon: true },
      })
      setPolyPts([]); setPolyMouse(null); setTool('select')
    }
  }

  // ── Save ──
  async function handleSave() {
    setSaving(true)
    try {
      const pxPerFt = calib ? dist(calib.p1.x,calib.p1.y,calib.p2.x,calib.p2.y)/calib.feet : null
      const saved = await saveSitePlan(isNew ? null : id!, {
        name: planName, version_label: versionLabel || null,
        address: address || null, map_snapshot_url: mapUrl,
        canvas_width: CANVAS_W, canvas_height: CANVAS_H,
        pixels_per_foot: pxPerFt, status: plan?.status ?? 'draft',
      }, items)
      if (isNew) navigate(`/site-plans/${saved.id}`, { replace: true })
      else { setPlan(saved); setSavedOk(true); setTimeout(()=>setSavedOk(false),2000) }
    } finally { setSaving(false) }
  }

  // ── Export PDF ──
  async function handleExportPDF() {
    const stage = stageRef.current; if (!stage) return
    const prev = { scale: stageScale, pos: stagePos }
    setStageScale(1); setStagePos({x:0,y:0})
    await new Promise(r => setTimeout(r, 80))
    const dataUrl = stage.toDataURL({ pixelRatio: 2 })
    setStageScale(prev.scale); setStagePos(prev.pos)
    const pdf = new jsPDF({ orientation:'landscape', unit:'in', format:'letter' })
    pdf.setFontSize(20); pdf.setTextColor('#2f7a3a')
    pdf.text("Thomson's Landscaping & Garden Center", 0.5, 0.7)
    pdf.setFontSize(14); pdf.setTextColor('#333'); pdf.text(planName, 0.5, 1.1)
    if (address) { pdf.setFontSize(11); pdf.setTextColor('#666'); pdf.text(address, 0.5, 1.45) }
    if (versionLabel) pdf.text(`Version: ${versionLabel}`, 0.5, 1.72)
    pdf.addPage()
    pdf.addImage(dataUrl, 'PNG', 0.25, 0.25, 10.5, 7.5)
    if (scheduleRows.length > 0) {
      pdf.addPage()
      pdf.setFontSize(13); pdf.setTextColor('#2f7a3a'); pdf.text('Plant Schedule', 0.5, 0.7)
      pdf.setFontSize(10); pdf.setTextColor('#333')
      let y = 1.1
      pdf.text('Sym',0.5,y); pdf.text('Plant',1.0,y); pdf.text('Spec',5.0,y)
      pdf.text('Qty',6.5,y); pdf.text('Unit',7.5,y); pdf.text('Ext',9.2,y)
      y+=0.1; pdf.line(0.5,y,10.5,y); y+=0.22
      scheduleRows.forEach(({item,qty})=>{
        pdf.text(item.symbol_code??'-',0.5,y)
        pdf.text((item.product_name??item.label??'').slice(0,46),1.0,y)
        pdf.text(item.size_spec??'',5.0,y); pdf.text(String(qty),6.5,y)
        pdf.text(item.unit_price!=null?currency(item.unit_price):'',7.5,y)
        pdf.text(item.unit_price!=null?currency(qty*item.unit_price):'',9.2,y)
        y+=0.27; if(y>7.5){pdf.addPage();y=0.7}
      })
      y+=0.12; pdf.line(0.5,y,10.5,y); y+=0.28
      pdf.setFont('helvetica','bold')
      pdf.text(`Total: ${currency(scheduleTotal)}`,7.5,y)
    }
    pdf.save(`${planName.replace(/\s+/g,'-')}.pdf`)
  }

  // ── Derived ──
  const schedule = items.filter(it=>it.item_type==='plant'&&it.product_name)
    .reduce<Record<string,{item:SitePlanItem;qty:number}>>((acc,it)=>{
      const k=String(it.product_id??it.product_name??it.id)
      if(acc[k]) acc[k].qty+=(it.qty??1); else acc[k]={item:it,qty:it.qty??1}
      return acc
    },{})
  const scheduleRows = Object.values(schedule)
  const scheduleTotal = scheduleRows.reduce((s,r)=>s+r.qty*(r.item.unit_price??0),0)
  const visibleItems = items.filter(it=>visibleLayers.has(it.layer as LayerType))
  const selectedItem = items.find(it=>it.id===selectedId)??null
  // Server already filters by paletteSearch; just cap the rendered count.
  const filtered = paletteItems.slice(0, 100)

  // Live preview geometry
  const previewRect = dragStart && dragCurrent ? {
    x:Math.min(dragStart.x,dragCurrent.x), y:Math.min(dragStart.y,dragCurrent.y),
    w:Math.abs(dragCurrent.x-dragStart.x), h:Math.abs(dragCurrent.y-dragStart.y),
  } : null
  const dimLive = dimStart && dimCurrent
    ? { pts:[dimStart.x,dimStart.y,dimCurrent.x,dimCurrent.y], label:pxToFt(dist(dimStart.x,dimStart.y,dimCurrent.x,dimCurrent.y),calib) }
    : null

  if (loading) return <Spinner label="Loading site plan…" />

  const cursor = calibMode!=='idle' ? 'crosshair' : tool==='select' ? 'default' : 'crosshair'
  const previewColor = tool==='bed'?'#6abf4b':'#b0a090'

  return (
    <div className="flex flex-col bg-gray-900" style={{height:'100vh',overflow:'hidden'}}>

      {/* ── Top bar ── */}
      <div className="h-12 bg-gray-800 border-b border-gray-700 flex items-center gap-2 px-3 shrink-0">
        <button onClick={()=>navigate('/site-plans')}
          className="flex items-center gap-1 text-gray-300 hover:text-white text-sm px-2 py-1 rounded hover:bg-gray-700">
          <ArrowLeft size={15}/> Back
        </button>
        <div className="w-px h-5 bg-gray-600 mx-1"/>
        <input className="bg-gray-700 text-white rounded px-2 py-1 text-sm font-semibold w-52 border border-gray-600 focus:border-green-500 outline-none"
          value={planName} onChange={e=>setPlanName(e.target.value)}/>
        <input className="bg-gray-700 text-gray-300 rounded px-2 py-1 text-sm w-28 border border-gray-600 focus:border-green-500 outline-none"
          value={versionLabel} onChange={e=>setVersionLabel(e.target.value)} placeholder="Version…"/>
        <div className="flex-1"/>
        {/* Zoom */}
        <button onClick={()=>setStageScale(s=>Math.max(MIN_SCALE,+(s*0.8).toFixed(2)))}
          className="w-7 h-7 rounded hover:bg-gray-700 text-gray-300 flex items-center justify-center"><ZoomOut size={14}/></button>
        <span className="text-gray-400 text-xs w-10 text-center">{Math.round(stageScale*100)}%</span>
        <button onClick={()=>setStageScale(s=>Math.min(MAX_SCALE,+(s*1.25).toFixed(2)))}
          className="w-7 h-7 rounded hover:bg-gray-700 text-gray-300 flex items-center justify-center"><ZoomIn size={14}/></button>
        <button onClick={()=>{setStageScale(1);setStagePos({x:0,y:0})}}
          className="text-xs text-gray-400 hover:text-white px-2">Reset</button>
        <div className="w-px h-5 bg-gray-600 mx-1"/>
        {/* Scale calibration */}
        <button onClick={()=>setShowCalib(s=>!s)}
          className={`text-xs px-2 py-1 rounded border ${calib?'border-green-500 text-green-400':'border-gray-600 text-gray-400'} hover:border-green-400`}>
          {calib ? '⚖ Scaled' : '⚖ Set Scale'}
        </button>
        <div className="w-px h-5 bg-gray-600 mx-1"/>
        <button onClick={handleUndo} title="Undo (Ctrl+Z)"
          className="w-7 h-7 rounded hover:bg-gray-700 text-gray-300 flex items-center justify-center"><RotateCcw size={14}/></button>
        <button onClick={handleExportPDF}
          className="flex items-center gap-1 text-gray-300 hover:text-white text-sm px-2 py-1 rounded hover:bg-gray-700">
          <Download size={14}/> PDF
        </button>
        <button onClick={handleSave} disabled={saving}
          className={`flex items-center gap-1 text-sm px-3 py-1 rounded font-medium transition-colors ${savedOk?'bg-green-600 text-white':saving?'bg-gray-600 text-gray-400':'bg-green-700 hover:bg-green-600 text-white'}`}>
          <Save size={14}/> {savedOk?'Saved!':saving?'Saving…':'Save'}
        </button>
      </div>

      {/* ── Scale calibration panel ── */}
      {showCalib && (
        <div className="bg-gray-800 border-b border-gray-700 px-4 py-2 flex items-center gap-3 text-sm shrink-0">
          <span className="text-gray-300 font-medium">Scale Calibration:</span>
          <span className="text-gray-400 text-xs">1. Enter a known distance, 2. Click two points on the plan that span that distance</span>
          <label className="text-gray-300 ml-2">Distance (ft):</label>
          <input className="bg-gray-700 text-white border border-gray-600 rounded px-2 py-0.5 w-20 text-sm"
            value={calibFeet} onChange={e=>setCalibFeet(e.target.value)}/>
          <button onClick={()=>setCalibMode('p1')}
            className={`px-3 py-1 rounded text-xs font-medium ${calibMode!=='idle'?'bg-yellow-600 text-white animate-pulse':'bg-blue-700 hover:bg-blue-600 text-white'}`}>
            {calibMode==='idle'?'Pick Points':calibMode==='p1'?'Click Point 1…':'Click Point 2…'}
          </button>
          {calib && <span className="text-green-400 text-xs">✓ Scale set: {pxToFt(100,calib)} = 100px</span>}
          {calib && <button onClick={()=>setCalib(null)} className="text-red-400 text-xs hover:text-red-300">Clear</button>}
        </div>
      )}

      <div className="flex flex-1 overflow-hidden">
        {/* ── Left toolbar ── */}
        <div className="w-14 bg-gray-800 border-r border-gray-700 flex flex-col items-center py-3 gap-1 shrink-0">
          {TOOL_BTNS.map(t=>(
            <button key={t.key} title={t.tip} onClick={()=>setTool(t.key)}
              className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors text-sm ${
                tool===t.key?'bg-green-600 text-white':'text-gray-400 hover:bg-gray-700 hover:text-white'
              }`}>
              {t.icon}
            </button>
          ))}
          <div className="border-t border-gray-700 w-8 my-2"/>
          <button title="Delete (Del)" onClick={deleteSelected}
            className="w-10 h-10 rounded-lg flex items-center justify-center text-red-400 hover:bg-red-900 hover:text-red-300">
            <X size={16}/>
          </button>
          {/* Active layer indicator */}
          <div className="mt-auto pb-2 flex flex-col items-center gap-1">
            <div className="w-8 h-1 rounded" style={{backgroundColor:LAYER_COLORS[activeLayer]}}/>
            <span className="text-gray-500 text-[9px] text-center leading-tight capitalize">{activeLayer}</span>
          </div>
        </div>

        {/* ── Canvas column ── */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Address + layer bar */}
          <div className="bg-gray-700 border-b border-gray-600 flex items-center gap-2 px-3 py-1.5 shrink-0">
            <Search size={13} className="text-gray-400"/>
            <input className="flex-1 bg-transparent text-gray-200 text-sm outline-none placeholder-gray-500"
              placeholder="Property address → Enter to load satellite map"
              value={address} onChange={e=>setAddress(e.target.value)}
              onKeyDown={e=>e.key==='Enter'&&loadMap()}/>
            <button onClick={loadMap}
              className="text-xs px-2 py-0.5 rounded bg-gray-600 hover:bg-gray-500 text-gray-200">
              {GOOGLE_MAPS_KEY?'Load Map':'No API Key'}
            </button>
            <div className="w-px h-4 bg-gray-600 mx-1"/>
            {/* Layer toggles */}
            <span className="text-gray-400 text-xs">Layers:</span>
            {(['existing','proposed','phase1','phase2'] as LayerType[]).map(l=>{
              const on=visibleLayers.has(l)
              return (
                <button key={l} title={`Toggle ${l}`}
                  onClick={()=>{const n=new Set(visibleLayers);on?n.delete(l):n.add(l);setVisibleLayers(n)}}
                  className="w-3 h-3 rounded-sm border transition-opacity"
                  style={{backgroundColor:LAYER_COLORS[l],borderColor:LAYER_COLORS[l],opacity:on?1:0.3}}/>
              )
            })}
            <span className="text-gray-400 text-xs ml-1">Active:</span>
            {(['proposed','phase1','phase2','existing'] as LayerType[]).map(l=>(
              <button key={l} onClick={()=>setActiveLayer(l)}
                className="text-xs px-1.5 py-0.5 rounded capitalize transition-colors"
                style={activeLayer===l?{backgroundColor:LAYER_COLORS[l],color:'#fff'}:{color:LAYER_COLORS[l]}}>
                {l}
              </button>
            ))}
          </div>

          {/* ── Konva Stage ── */}
          <div className="flex-1 overflow-hidden bg-gray-900 relative">
            {!mapImage && (
              <div className="absolute inset-0 flex items-center justify-center text-gray-600 text-sm pointer-events-none select-none">
                Enter address above to load satellite view, or draw on blank canvas.
                {!GOOGLE_MAPS_KEY && <span className="ml-2 text-yellow-600"> (Add VITE_GOOGLE_MAPS_KEY to .env)</span>}
              </div>
            )}
            {/* Polygon in-progress hint */}
            {tool==='polygon' && polyPts.length>0 && (
              <div className="absolute top-2 left-1/2 -translate-x-1/2 bg-black/70 text-white text-xs px-3 py-1 rounded-full pointer-events-none">
                {polyPts.length/2} points — double-click to close, Esc to cancel
              </div>
            )}
            <Stage ref={stageRef}
              width={window.innerWidth-14*4-288}
              height={window.innerHeight-48-36}
              scaleX={stageScale} scaleY={stageScale}
              x={stagePos.x} y={stagePos.y}
              style={{cursor, display:'block'}}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onClick={handleClick}
              onDblClick={handleDblClick}
              onWheel={handleWheel}
            >
              {/* Background */}
              <Layer>
                {mapImage && <KonvaImage image={mapImage} width={CANVAS_W} height={CANVAS_H}/>}
                {!mapImage && <Rect x={0} y={0} width={CANVAS_W} height={CANVAS_H} fill="#1a1a2e"/>}
                {/* Grid lines */}
                {Array.from({length:Math.ceil(CANVAS_W/50)+1},(_,i)=>(
                  <Line key={`gx${i}`} points={[i*50,0,i*50,CANVAS_H]} stroke="#ffffff08" strokeWidth={1}/>
                ))}
                {Array.from({length:Math.ceil(CANVAS_H/50)+1},(_,i)=>(
                  <Line key={`gy${i}`} points={[0,i*50,CANVAS_W,i*50]} stroke="#ffffff08" strokeWidth={1}/>
                ))}
              </Layer>

              {/* Items layer */}
              <Layer>
                {visibleItems.map(item=>{
                  const sel=item.id===selectedId
                  const base={item,isSelected:sel,onSelect:()=>setSelectedId(item.id)}
                  const onChange=(attrs:Record<string,unknown>)=>changeItemAttrs(item.id,attrs)
                  if(item.item_type==='plant') return <PlantSymbol key={item.id} {...base} onChange={onChange}/>
                  if(item.item_type==='bed'&&!item.attrs.isPolygon) return <RectShape key={item.id} {...base} onChange={onChange}/>
                  if(item.item_type==='hardscape') return <RectShape key={item.id} {...base} onChange={onChange}/>
                  if(item.item_type==='bed'&&item.attrs.isPolygon) return <PolyShape key={item.id} {...base} onChange={onChange}/>
                  if(item.item_type==='dimension') return <DimShape key={item.id} {...base} calib={calib} onSelect={()=>setSelectedId(item.id)}/>
                  if(item.item_type==='label') return <LabelShape key={item.id} {...base} onChange={onChange}/>
                  return null
                })}

                {/* Live drag preview (bed/hardscape) */}
                {previewRect && previewRect.w>2 && previewRect.h>2 && (
                  <Rect x={previewRect.x} y={previewRect.y} width={previewRect.w} height={previewRect.h}
                    fill={previewColor+'33'} stroke={previewColor} strokeWidth={1.5}
                    dash={[6,3]} listening={false}/>
                )}

                {/* Live dimension preview */}
                {dimLive && (
                  <>
                    <Arrow points={dimLive.pts} stroke="#facc15" strokeWidth={1.5} fill="#facc15"
                      pointerLength={8} pointerWidth={6} pointerAtBeginning listening={false}/>
                    <Rect x={(dimLive.pts[0]+dimLive.pts[2])/2-dimLive.label.length*3.5-2}
                      y={(dimLive.pts[1]+dimLive.pts[3])/2-10}
                      width={dimLive.label.length*7+4} height={16}
                      fill="rgba(0,0,0,0.7)" cornerRadius={3} listening={false}/>
                    <Text x={(dimLive.pts[0]+dimLive.pts[2])/2-dimLive.label.length*3.5}
                      y={(dimLive.pts[1]+dimLive.pts[3])/2-9}
                      text={dimLive.label} fontSize={11} fill="#facc15" fontStyle="bold" listening={false}/>
                  </>
                )}

                {/* Polygon in-progress */}
                {polyPts.length>=2 && polyMouse && (
                  <>
                    <Line points={[...polyPts,polyMouse.x,polyMouse.y]} stroke="#6abf4b"
                      strokeWidth={1.5} dash={[6,3]} listening={false}/>
                    {Array.from({length:polyPts.length/2},(_,i)=>(
                      <Circle key={i} x={polyPts[i*2]} y={polyPts[i*2+1]} radius={4}
                        fill="#6abf4b" stroke="#fff" strokeWidth={1} listening={false}/>
                    ))}
                  </>
                )}

                {/* Calibration points */}
                {calibP1 && calibMode==='p2' && (
                  <Circle x={calibP1.x} y={calibP1.y} radius={5} fill="#facc15" stroke="#fff" strokeWidth={1.5} listening={false}/>
                )}
              </Layer>
            </Stage>
          </div>

          {/* Status bar */}
          <div className="h-6 bg-gray-800 border-t border-gray-700 flex items-center px-3 gap-4 text-xs text-gray-500 shrink-0">
            <span>Tool: <span className="text-gray-300 capitalize">{tool}</span></span>
            <span>Items: <span className="text-gray-300">{items.length}</span></span>
            {calib && <span className="text-green-500">Scale active — drag Measure tool to read ft</span>}
            {!calib && <span className="text-yellow-600">No scale set — measurements in px</span>}
            <span className="ml-auto">Shortcuts: V select · B bed · H hardscape · G polygon · D measure · L label · Del delete · Ctrl+Z undo</span>
          </div>
        </div>

        {/* ── Right panel ── */}
        <div className="w-72 bg-gray-800 border-l border-gray-700 flex flex-col shrink-0 overflow-hidden">
          {/* Tabs */}
          <div className="flex border-b border-gray-700">
            {(['palette','schedule'] as const).map(tab=>(
              <button key={tab} onClick={()=>setActiveTab(tab)}
                className={`flex-1 py-2 text-xs font-semibold transition-colors ${
                  activeTab===tab?'border-b-2 border-green-500 text-green-400':'text-gray-500 hover:text-gray-300'
                }`}>
                {tab==='palette'?'🌿 Palette':`📋 Schedule (${scheduleRows.length})`}
              </button>
            ))}
          </div>

          {/* Palette */}
          {activeTab==='palette' && (<>
            <div className="p-2 border-b border-gray-700">
              <div className="flex items-center gap-1 bg-gray-700 border border-gray-600 rounded px-2">
                <Search size={12} className="text-gray-500"/>
                <input className="flex-1 py-1.5 text-xs outline-none bg-transparent text-gray-200 placeholder-gray-500"
                  placeholder="Search plants & materials…"
                  value={paletteSearch} onChange={e=>setPaletteSearch(e.target.value)}/>
                {paletteSearch && <button onClick={()=>setPaletteSearch('')}><X size={11} className="text-gray-500"/></button>}
              </div>
              {selectedProduct && (
                <div className="mt-2 p-2 bg-green-900/40 border border-green-700 rounded text-xs">
                  <div className="text-green-300 font-semibold truncate">{String(selectedProduct.product_name??selectedProduct.name??'')}</div>
                  <div className="text-green-500 mt-0.5">Click on canvas to place · P to activate</div>
                </div>
              )}
            </div>
            <div className="flex-1 overflow-y-auto">
              {filtered.length===0 && <div className="p-4 text-xs text-gray-600 text-center">No products found</div>}
              {filtered.map(p=>{
                const name=String(p.product_name??p.name??'')
                const inPlan=scheduleRows.find(r=>r.item.product_id===p.id)
                const isSel=selectedProduct?.id===p.id
                return (
                  <button key={p.id}
                    onClick={()=>{setSelectedProduct(isSel?null:p);if(!isSel)setTool('plant')}}
                    className={`w-full text-left px-3 py-2 border-b border-gray-700/50 flex items-start gap-2 transition-colors ${
                      isSel?'bg-green-900/40 border-l-2 border-l-green-500':'hover:bg-gray-700/50'
                    }`}>
                    <Leaf size={13} className="mt-0.5 text-green-500 shrink-0"/>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-gray-200 truncate">{name}</div>
                      {p.botanical_name!=null && <div className="text-xs text-gray-500 italic truncate">{String(p.botanical_name)}</div>}
                      <div className="flex items-center gap-2 mt-0.5">
                        {p.retail_price!=null && <span className="text-xs text-green-400 font-medium">{currency(Number(p.retail_price))}</span>}
                        {inPlan && <span className="text-xs text-blue-400">×{inPlan.qty} on plan</span>}
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>
          </>)}

          {/* Schedule */}
          {activeTab==='schedule' && (
            <div className="flex-1 overflow-y-auto">
              {scheduleRows.length===0
                ? <div className="p-4 text-xs text-gray-600 text-center">Place plants on the canvas.</div>
                : (<>
                  {scheduleRows.map(({item,qty})=>(
                    <div key={item.product_id??item.id} className="border-b border-gray-700/50 px-3 py-2">
                      <div className="flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0"
                          style={{backgroundColor:item.color??'#22c55e'}}>
                          {item.symbol_code??'?'}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-medium text-gray-200 truncate">{item.product_name??item.label}</div>
                          {item.size_spec && <div className="text-xs text-gray-500">{item.size_spec}</div>}
                        </div>
                      </div>
                      <div className="mt-1 flex justify-between text-xs text-gray-500 pl-8">
                        <span>×{qty}</span>
                        {item.unit_price!=null && <span>{currency(item.unit_price)}</span>}
                        {item.unit_price!=null && <span className="text-gray-300 font-semibold">{currency(qty*item.unit_price)}</span>}
                      </div>
                    </div>
                  ))}
                  <div className="px-3 py-3 flex justify-between text-sm font-semibold border-t border-gray-700 text-gray-200">
                    <span>Total</span><span className="text-green-400">{currency(scheduleTotal)}</span>
                  </div>
                </>)
              }
            </div>
          )}

          {/* Selected item properties */}
          {selectedItem && (
            <div className="border-t border-gray-700 p-3 bg-gray-750 text-xs space-y-2 shrink-0" style={{backgroundColor:'#1f2937'}}>
              <div className="font-semibold text-gray-300 flex items-center justify-between">
                <span>Selected: <span className="text-gray-400 capitalize">{selectedItem.item_type}</span></span>
                <button onClick={deleteSelected} className="text-red-400 hover:text-red-300 text-xs">Delete</button>
              </div>
              <div className="flex gap-2 items-center">
                <label className="text-gray-500 w-10 shrink-0">Label</label>
                <input className="flex-1 bg-gray-700 border border-gray-600 rounded px-1.5 py-0.5 text-gray-200 outline-none focus:border-green-500"
                  value={selectedItem.label??''}
                  onChange={e=>setItems(prev=>prev.map(it=>it.id===selectedItem.id?{...it,label:e.target.value}:it))}/>
              </div>
              {selectedItem.item_type==='plant' && (<>
                <div className="flex gap-2 items-center">
                  <label className="text-gray-500 w-10 shrink-0">Size</label>
                  <input className="flex-1 bg-gray-700 border border-gray-600 rounded px-1.5 py-0.5 text-gray-200 outline-none focus:border-green-500"
                    value={selectedItem.size_spec??''} placeholder="e.g. 3 gal."
                    onChange={e=>setItems(prev=>prev.map(it=>it.id===selectedItem.id?{...it,size_spec:e.target.value}:it))}/>
                </div>
                <div className="flex gap-2 items-center">
                  <label className="text-gray-500 w-10 shrink-0">Qty</label>
                  <button onClick={()=>setItems(prev=>prev.map(it=>it.id===selectedItem.id?{...it,qty:Math.max(0,(it.qty??1)-1)}:it))}
                    className="w-6 h-6 bg-gray-700 rounded flex items-center justify-center text-gray-300 hover:bg-gray-600"><Minus size={11}/></button>
                  <span className="w-8 text-center font-medium text-gray-200">{selectedItem.qty??1}</span>
                  <button onClick={()=>setItems(prev=>prev.map(it=>it.id===selectedItem.id?{...it,qty:(it.qty??1)+1}:it))}
                    className="w-6 h-6 bg-gray-700 rounded flex items-center justify-center text-gray-300 hover:bg-gray-600"><Plus size={11}/></button>
                </div>
                <div className="flex gap-2 items-center">
                  <label className="text-gray-500 w-10 shrink-0">Size</label>
                  <input type="range" min="10" max="80"
                    value={(selectedItem.attrs.radius as number)??28}
                    onChange={e=>changeItemAttrs(selectedItem.id,{...selectedItem.attrs,radius:Number(e.target.value)})}
                    className="flex-1"/>
                  <span className="text-gray-400 w-8">{(selectedItem.attrs.radius as number)??28}px</span>
                </div>
              </>)}
              {(selectedItem.item_type==='bed'||selectedItem.item_type==='hardscape') && !selectedItem.attrs.isPolygon && (
                <div className="text-gray-500 text-xs">
                  W: {Math.round(selectedItem.attrs.width as number??0)}px ·
                  H: {Math.round(selectedItem.attrs.height as number??0)}px
                  {calib && <span className="text-green-400 ml-1">
                    ({pxToFt(selectedItem.attrs.width as number??0,calib)} × {pxToFt(selectedItem.attrs.height as number??0,calib)})
                  </span>}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
